import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import logo from "../../assets/images/brand/logo.png";
import logoWhite from "../../assets/images/brand/logo-white.png";
import { useBookingModal } from "../../contexts/BookingModalContext";

const navigation = [
  { name: "Home", href: "/" },
  {
    name: "Design Layouts",
    href: "#",
    children: [
      { name: "Kitchen", href: "/rooms/kitchen" },
      { name: "Living Room", href: "/rooms/living" },
      { name: "Bedroom", href: "/rooms/bedroom" },
      { name: "Wardrobe", href: "/rooms/wardrobe" },
      { name: "Dining", href: "/rooms/dining" },
      { name: "Kids Room", href: "/rooms/kids" },
      { name: "Study", href: "/rooms/study" },
      { name: "Pooja Room", href: "/rooms/pooja" },
      { name: "Space Saving Furniture", href: "/rooms/space-saving" },
      { name: "False Ceiling", href: "/rooms/false-ceiling" },
      { name: "Doors", href: "/rooms/doors" },
      { name: "Lighting", href: "/rooms/lighting" },
    ],
  },
  { name: "Process", href: "/process" },
  { name: "Factory", href: "/factory" },
  { name: "Gallery", href: "/gallery" },
  { name: "About", href: "/about" },
  { name: "Blog", href: "/blog" },
  { name: "Contact", href: "/contact" },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();
  const { openModal } = useBookingModal();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#122367] border-b border-white/10 shadow-large py-3 lg:py-2 transition-all duration-500">
        <nav className="flex items-center justify-between px-4 sm:px-6 lg:px-8 xl:px-12">
          {/* Logo */}
          <Link to="/" className="flex flex-col group">
            <img src={logoWhite} alt="Iravatham" className="h-20 lg:h-14 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <div key={item.name} className="relative">
                {item.children ? (
                  <div
                    className="relative"
                    onMouseEnter={() => setOpenDropdown(item.name)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <button
                      className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                        isActive("/rooms")
                          ? "text-brand-accent"
                          : "text-white hover:text-brand-accent"
                      }`}
                    >
                      {item.name}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${
                          openDropdown === item.name ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    <div
                      className={`absolute top-full left-0 pt-2 transition-all duration-200 ${
                        openDropdown === item.name
                          ? "opacity-100 visible translate-y-0"
                          : "opacity-0 invisible -translate-y-2"
                      }`}
                    >
                      <div className="bg-white rounded-xl shadow-large border border-border-light p-2 min-w-[200px]">
                        {item.children.map((child) => (
                          <Link
                            key={child.name}
                            to={child.href}
                            className={`block px-4 py-2.5 text-sm rounded-lg transition-colors ${
                              isActive(child.href)
                                ? "bg-brand-accent/10 text-brand-accent"
                                : "text-text-primary hover:bg-background-card hover:text-brand-accent"
                            }`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.href}
                    className={`px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                      isActive(item.href)
                        ? "text-brand-accent"
                        : "text-white hover:text-brand-accent"
                    }`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <button onClick={openModal} className="btn-primary">
              Book Consultation
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2.5 transition-all relative z-10 rounded-lg text-white bg-white/10 hover:bg-white/20 border border-white/20"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </nav>
      </header>

      {/* Mobile Menu Modal - Outside header to avoid positioning constraints */}
      {isMobileMenuOpen && (
        <>
          {/* Opaque Backdrop */}
          <div
            className="lg:hidden fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] transition-opacity duration-300"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Mobile Menu Modal */}
          <div className="lg:hidden fixed inset-x-4 top-24 bottom-4 bg-white rounded-3xl shadow-2xl z-[70] transition-all duration-300 overflow-hidden">
            <div className="h-full flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-border-light">
                <h2 className="font-heading text-xl text-brand-primary">
                  Menu
                </h2>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 text-text-secondary hover:text-brand-primary transition-colors"
                  aria-label="Close menu"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Menu Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-2">
                {navigation.map((item) => (
                  <div key={item.name}>
                    {item.children ? (
                      <div>
                        <button
                          className="flex items-center justify-between w-full px-4 py-3 text-left text-text-primary font-medium hover:bg-background-card rounded-lg transition-colors"
                          onClick={() =>
                            setOpenDropdown(
                              openDropdown === item.name ? null : item.name,
                            )
                          }
                        >
                          {item.name}
                          <ChevronDown
                            className={`w-5 h-5 transition-transform ${
                              openDropdown === item.name ? "rotate-180" : ""
                            }`}
                          />
                        </button>
                        <div
                          className={`pl-4 space-y-1 overflow-hidden transition-all duration-200 ${
                            openDropdown === item.name
                              ? "max-h-[400px] pb-2"
                              : "max-h-0"
                          }`}
                        >
                          {item.children.map((child) => (
                            <Link
                              key={child.name}
                              to={child.href}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className={`block px-4 py-2.5 rounded-lg transition-colors ${
                                isActive(child.href)
                                  ? "bg-brand-accent/10 text-brand-accent"
                                  : "text-text-secondary hover:bg-background-card hover:text-brand-accent"
                              }`}
                            >
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        to={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`block px-4 py-3 font-medium rounded-lg transition-colors ${
                          isActive(item.href)
                            ? "bg-brand-accent/10 text-brand-accent"
                            : "text-text-primary hover:bg-background-card hover:text-brand-accent"
                        }`}
                      >
                        {item.name}
                      </Link>
                    )}
                  </div>
                ))}
              </div>

              {/* Modal Footer */}
              <div className="p-6 border-t border-border-light">
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    openModal();
                  }}
                  className="btn-primary w-full justify-center"
                >
                  Book Consultation
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
